import { SCALING_TABLE_TYPE } from '@/main/app.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import _ from 'lodash';
import { TableTypeEnum } from '@/sdk/model/TableDefinitionInputV1';
import type { SelectedSearchItem } from '@/search/search.types';
import {
  HierarchyNodeOutputV1,
  HierarchyOutputV1,
  ItemPreviewV1,
  ItemSearchPreviewV1,
  sqHierarchiesApi,
  sqTableDefinitionsApi,
} from '@/sdk';
import { ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionOutputV1';
import { processMaterializedTable } from '@/tableDefinitionEditor/processedMaterializedTable.utilities';
import { ItemTableCell, ProcessedTableCell } from '@/tableDefinitionEditor/tableDefinition.types';
import {
  getColumnsNotIncludedInAgGrid,
  getMaterializedTable,
  isItemTableCell,
} from '@/tableDefinitionEditor/tableDefinition.utilities';
import { HierarchyNodeType } from '@/search/search.constants';
import { TableDefinitionOutputV1 } from '@/sdk/model/TableDefinitionOutputV1';
import { getAllItems } from '@/trend/trendDataHelper.utilities';
import { convertItemIdColumnName } from '@/trendData/trend.utilities';
import { GraphQL } from '@/serverSideTable/materializedTables/materializedTables.constants';

/**
 * Checks if search types are invalid.
 * An invalid result is defined as combining {@link SCALING_TABLE_TYPE} with other search types.
 */
export const hasInValidSearchTypes = (types: string[]) => types.length > 1 && types.includes(SCALING_TABLE_TYPE);

/*
 * Limit of items shown in the overview mode of the Search Widget
 */
export const LIMIT_IN_OVERVIEW = 8;

export const replaceScalingPseudoTypeWithBackendType = (searchTypes: string[] | string): string | string[] => {
  if (!searchTypes) {
    return searchTypes;
  }
  if (!_.isArray(searchTypes)) {
    return searchTypes === SCALING_TABLE_TYPE ? SeeqNames.Types.TableDefinition : searchTypes;
  }
  return _.map(searchTypes, (type) => (type === SCALING_TABLE_TYPE ? SeeqNames.Types.TableDefinition : type));
};

const SCALING_CSV_FILTER = `&&${SeeqNames.Properties.TableType}==${TableTypeEnum.SCALINGCSV}`;
const SCALING_FILTER = `&&${SeeqNames.Properties.TableType}==${TableTypeEnum.SCALING}`;
const ITEM_CSV_FILTER = `&&${SeeqNames.Properties.TableType}==${TableTypeEnum.ITEMCSV}`;
const ITEM_FILTER = `&&${SeeqNames.Properties.TableType}==${TableTypeEnum.ITEM}`;

const transformSingleFilterToIncludeTableType = (filter: string, type: 'Scaling' | 'Item'): string[] => {
  if (type === 'Scaling') {
    return [filter.concat(SCALING_FILTER), filter.concat(SCALING_CSV_FILTER)];
  } else {
    return [filter.concat(ITEM_FILTER), filter.concat(ITEM_CSV_FILTER)];
  }
};

export const addFilterForTableDefinitionSubTypes = (
  searchTypes: string[] | string,
  currentFilters: string[] | string,
  type: 'Scaling' | 'Item',
): string | string[] => {
  if (!_.includes(searchTypes, SeeqNames.Types.TableDefinition)) {
    return currentFilters;
  }
  if (typeof currentFilters === 'string') {
    if (currentFilters.includes(SeeqNames.API.Flags.ExcludeGloballyScoped)) {
      return [currentFilters].concat(transformSingleFilterToIncludeTableType('', type));
    }
    return transformSingleFilterToIncludeTableType(currentFilters, type);
  }

  const [potentialExcludeGloballyScoped, filters] = _.partition(
    currentFilters,
    (filter) => filter === SeeqNames.API.Flags.ExcludeGloballyScoped,
  );
  // If the search type is TableDefinition, but filters are empty, we still need to add the filters.
  const filtersToProcess = filters.length === 0 ? [''] : filters;
  return potentialExcludeGloballyScoped.concat(
    filtersToProcess.flatMap((filter) => transformSingleFilterToIncludeTableType(filter, type)),
  );
};

export const getScalingTableLayerForDataTab = (
  searchItem: SelectedSearchItem | ItemSearchPreviewV1,
  scalingTableIsCsv = false,
): Promise<SelectedSearchItem[]> => {
  if (searchItem.type !== SeeqNames.Types.TableDefinition) {
    const scalingTable = searchItem.ancestors!.find((ancestor) => ancestor.type === SeeqNames.Types.TableDefinition);
    return getScalingTableRowAsChildren(searchItem, scalingTable!);
  }
  return getScalingTableIndependentVariables(searchItem, scalingTableIsCsv);
};

/**
 * Fetches the independent variables from a scaling table.
 * @param scalingTableDefinition
 * @param scalingTableIsCsv
 */
const getScalingTableIndependentVariables = async (
  scalingTableDefinition: SelectedSearchItem | ItemSearchPreviewV1,
  scalingTableIsCsv = false,
): Promise<SelectedSearchItem[]> => {
  const includedColumn = scalingTableIsCsv
    ? SeeqNames.MaterializedTables.CsvItemIdColumn
    : SeeqNames.MaterializedTables.ItemIdColumn;

  const { data } = await getMaterializedTable(
    scalingTableDefinition.id,
    [includedColumn],
    [
      {
        uuidColumn: includedColumn,
        propertyNames: ['Name', 'Type'],
      },
    ],
  );

  return _.chain(data?.table.rows)
    .filter((row: any) => {
      return row[0] !== null;
    })
    .map((row: any) => {
      return {
        id: row[0],
        name: row[1],
        type: row[2],
        ancestors: [
          {
            id: scalingTableDefinition.id,
            name: scalingTableDefinition.name,
            type: scalingTableDefinition.type,
          },
        ],
      } as SelectedSearchItem;
    })
    .sortBy('name')
    .value();
};

/**
 * Fetches the whole row of cell values from a scaling table of trendable columns.
 * @param tableSearchItem
 * @param scalingTableDefinition
 */
const getScalingTableRowAsChildren = async (
  tableSearchItem: SelectedSearchItem | ItemSearchPreviewV1,
  scalingTableDefinition: ItemPreviewV1,
): Promise<SelectedSearchItem[]> => {
  const { materializedTableRow } = await getItemsInScalingTableRow(scalingTableDefinition.id, tableSearchItem.id);

  return (
    _.chain(materializedTableRow)
      ?.filter((cell: ItemTableCell) => cell.fetchedProperties?.Type !== SeeqNames.Types.Asset)
      .map((cell: ItemTableCell) => {
        return {
          id: cell.uuid,
          name: cell.fetchedProperties?.Name,
          type: cell.fetchedProperties?.Type,
          ancestors: [
            ...tableSearchItem.ancestors!,
            {
              id: tableSearchItem.id,
              name: tableSearchItem.name,
              type: tableSearchItem.type,
            },
          ],
        } as SelectedSearchItem;
      })
      .sortBy('name')
      .value() || []
  );
};

/**
 * Fetches the whole row of cell values from a scaling table, only returning item cells.
 * @param tableDefinitionId
 * @param rowId - the id of the cell in the item id or csv item id column
 */
export const getItemsInScalingTableRow = async (
  tableDefinitionId: string,
  rowId: string,
): Promise<{ materializedTableRow: ItemTableCell[]; scalingTableData: TableDefinitionOutputV1 }> => {
  const { data: scalingTableData } = await sqTableDefinitionsApi.getTableDefinition({ id: tableDefinitionId });
  const scalingTableIsCsv = scalingTableData.tableType === TableTypeEnum.SCALINGCSV;
  const columnsNotIncludedInAgGrid = getColumnsNotIncludedInAgGrid(
    scalingTableData.tableType,
    scalingTableData.columnDefinitions,
  );
  const uuidColumns = scalingTableData.columnDefinitions
    .filter((column) => column.columnType === ColumnTypeEnum.UUID)
    .filter((column) => !columnsNotIncludedInAgGrid.includes(column.columnName));

  const { data } = await getMaterializedTable(
    tableDefinitionId,
    uuidColumns.map((column) => column.columnName),
    uuidColumns.map((column) => ({
      uuidColumn: column.columnName,
      propertyNames: [SeeqNames.Properties.Name, SeeqNames.Properties.Type],
    })),
    undefined,
    undefined,
    undefined,
    {
      valueFilter: {
        filterType: GraphQL.FilterTypes.Equals,
        columnName: scalingTableIsCsv
          ? SeeqNames.MaterializedTables.CsvItemIdColumn
          : SeeqNames.MaterializedTables.ItemIdColumn,
        value: rowId,
      },
    },
  );

  const processedMaterializedTable = processMaterializedTable(
    data?.table,
    scalingTableData.columnDefinitions.map((column) => {
      return { ...column, displayName: column.columnName, displayProperty: SeeqNames.Properties.Name };
    }),
  );

  return {
    materializedTableRow: processedMaterializedTable?.rows[0]
      .map((row, index) => {
        return {
          ...row,
          columnName: processedMaterializedTable.headers[index].name,
        };
      })
      .filter((cell: ProcessedTableCell) => isItemTableCell(cell)) as ItemTableCell[],
    scalingTableData,
  };
};

const nodeToSelectedSearchItem = (node: HierarchyNodeOutputV1, ancestors: SelectedSearchItem[]): SelectedSearchItem => {
  const id = node.itemMetadata ? node.itemMetadata.id : `${node.displayName}-${node.columnId}`;
  return {
    id,
    node,
    name: node.displayName,
    type: HierarchyNodeType,
    ancestors,
  } as SelectedSearchItem;
};

/**
 * Converts a hierarchy node and its path to a SelectedSearchItem, including all ancestors.
 * @param node - current node
 * @param nodePath = path from root to the current node
 * @param hierarchyOutput - hierarchy this node path lives in
 */
export const nodeAndPathToSelectedSearchItem = (
  node: HierarchyNodeOutputV1,
  nodePath: HierarchyNodeOutputV1[],
  hierarchyOutput: HierarchyOutputV1,
): SelectedSearchItem => {
  const nodePathAsAncestors: SelectedSearchItem[] = [hierarchyOutput];
  for (let i = 0; i < nodePath.length; i++) {
    const pathNode = nodePath[i];
    const thisAncestorAsSelectedSearchItem = nodeToSelectedSearchItem(pathNode, _.cloneDeep(nodePathAsAncestors));
    nodePathAsAncestors.push(thisAncestorAsSelectedSearchItem);
  }
  return nodeToSelectedSearchItem(node, nodePathAsAncestors);
};

/**
 * Fetches the children of a hierarchy node, and return them as SelectedSearchItems with ancestors.
 * @param hierarchyId - id of the hierarchy
 * @param currentItemWithNode - current item with node and ancestors
 * @param currentNodePath - path from root to the current node
 */
export const getHierarchyLayerAsItemsWithAncestors = async (
  hierarchyId: string,
  currentItemWithNode: SelectedSearchItem,
  currentNodePath: HierarchyNodeOutputV1[],
): Promise<SelectedSearchItem[]> => {
  const columnIds = currentNodePath.length > 0 ? currentNodePath.map((node) => node.columnId) : undefined;
  const columnValues =
    currentNodePath.length > 0 ? currentNodePath.map((node) => node.itemMetadata?.id ?? node.displayName) : undefined;
  const response = await sqHierarchiesApi.getHierarchyNodes({
    id: hierarchyId,
    columnIds,
    columnValues,
  });
  const nodes = response.data.nodes;

  return (
    nodes?.map((node) => {
      const ancestors = [
        ...(currentItemWithNode.ancestors || []),
        {
          id: currentItemWithNode.id,
          name: currentItemWithNode.name,
          type: currentItemWithNode.type,
          node: currentItemWithNode.node,
        },
      ];
      return nodeToSelectedSearchItem(node, ancestors);
    }) || []
  );
};

export const getScalingDetailsItemsWithColumnId = async () => {
  const scalingItems = getAllItems({ onlyScalingItems: true });

  if (scalingItems.length === 0) {
    return [];
  }

  const tableDefinition = await sqTableDefinitionsApi
    .getTableDefinition({ id: scalingItems[0].tableId })
    .then((res) => res.data);
  return scalingItems.map((item) => ({
    ...item,
    columnId: tableDefinition.columnDefinitions.find(
      (def) => convertItemIdColumnName(def.columnName) === convertItemIdColumnName(item.columnName),
    )?.id,
  }));
};
